import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { PencilSimpleIcon } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { TrashIcon } from '@phosphor-icons/react/dist/csr/Trash';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { CodeFormDialog } from './CodeFormDialog';
import { PageActions } from '../../app/shell/PageActions';
import { FilterBar } from '../../design-system/composites/FilterBar/FilterBar';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import {
  DataTable,
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import { ApiError } from '../../lib/api/client';
import {
  codeState,
  codesApi,
  type CodeKind,
  type CodeQuery,
  type CodeTypeCode,
  type DispensaryCode,
} from '../../lib/api/codes';
import styles from './CodeList.module.css';
import { optionalReferenceName } from '../../lib/format/referenceName';

/**
 * The dispensary code pool list — filters, cursor-paginated table, add form.
 * Follows `AuditLogScreen.tsx`/`SensorList.tsx` for the paginator (draft vs.
 * applied filters, an opaque cursor read out of `meta.next_cursor` and
 * handed back verbatim, never parsed).
 *
 * ## `state` renders the WHOLE filtered set, never a client-side narrowing
 *
 * `state` (free/bound) is sent to the server exactly as `dispensary`/`kind`/
 * `type` are — `codesApi.list` does the filtering; this component never
 * re-derives `codeState()` to hide a row the server already returned. A
 * mutant that dropped `state` from the outgoing query would still satisfy a
 * test that only checks a wanted row is present; task 7's own test asserts
 * on the ENTIRE rendered row set for exactly this reason.
 *
 * ## Delete is offered only on a FREE row, confirms inside a `Dialog`, and a
 * 409 reads differently from everything else
 *
 * `DispensaryCodeController::destroy()` refuses a bound code with a `409`
 * (`codes.ts`'s own docblock on `remove()`) — this list never even renders
 * the control on a bound row, and if a code is bound between this render and
 * the click anyway (another agent claimed it a moment ago), the resulting
 * `409` gets its own sentence (`codes.deleteConflict`), distinct from every
 * other failure (`codes.deleteFailed`), because "this code has already gone
 * to a patient" and "something else went wrong" call for different next
 * actions from an operator.
 *
 * Review finding 3 (task 7, round 2): an unbound code is still a saleable
 * voucher string, and destroying it is permanent — the ghost "Delete" button
 * in a dense table row therefore opens `ReferenceLifecycleActions.tsx`'s own
 * confirm-inside-a-still-open-`Dialog` shape (the identical `destructive`
 * dialog, kept open on failure so the 409's own sentence stays readable)
 * rather than acting on the first click. There is no `remove()` call
 * anywhere in this file that is not gated behind a confirmed dialog.
 */

export interface CodeListProps {
  /** Fired after a create or a delete succeeds — `CodeScreen`'s cue to
   *  refresh the availability summary, which a code's free/bound state
   *  itself affects. */
  onChanged?: () => void;
  /** Whether the pool tab is showing. The list stays mounted while hidden
   *  (`CodeScreen`'s docblock), so its Add command — portalled into the
   *  shell's title row — must leave with the tab. Defaults to true. */
  active?: boolean;
}

interface CodeRow extends Row, DispensaryCode {}

function toRow(node: DispensaryCode): CodeRow {
  return { ...node, id: node.uuid };
}

const KINDS: readonly CodeKind[] = ['reader', 'sensor'];
const TYPES: readonly CodeTypeCode[] = ['libre_2', 'libre_3', 'libre_2_plus', 'libre_3_plus'];

interface FilterState {
  dispensary: string;
  kind: string;
  type: string;
  state: string;
}

const BLANK_FILTERS: FilterState = { dispensary: '', kind: '', type: '', state: '' };

function toQuery(filters: FilterState, cursor?: string, perPage?: number): CodeQuery {
  const query: CodeQuery = {};
  if (filters.dispensary.trim() !== '') query.dispensary = filters.dispensary.trim();
  if (filters.kind !== '') query.kind = filters.kind as CodeKind;
  if (filters.type !== '') query.type = filters.type as CodeTypeCode;
  if (filters.state !== '') query.state = filters.state as 'free' | 'bound';
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;
  return query;
}

function hasAppliedFilters(query: CodeQuery): boolean {
  return (
    query.dispensary !== undefined ||
    query.kind !== undefined ||
    query.type !== undefined ||
    query.state !== undefined
  );
}

export function CodeList({ onChanged, active = true }: CodeListProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly CodeRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  // 50 — `CodeIndexRequest::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;
  const [draftFilters, setDraftFilters] = useState<FilterState>(BLANK_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(BLANK_FILTERS);

  const [formOpen, setFormOpen] = useState(false);
  /** The row being corrected; `undefined` while the dialog is adding one. */
  const [editing, setEditing] = useState<DispensaryCode | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<DispensaryCode | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState<string | null>(null);
  const [deleteReference, setDeleteReference] = useState<string | null>(null);

  const requestSeq = useRef(0);

  const load = useCallback(
    async (query: CodeQuery) => {
      const seq = ++requestSeq.current;
      setStatus('loading');
      setReference(null);

      try {
        const page = await codesApi.list(query);
        if (seq !== requestSeq.current) return;
        setRows(page.data.map(toRow));
        // `CodePage.meta` carries no `total` — see the `<Pagination total={null}>`
        // calls below.
        observe({ next_cursor: page.meta.next_cursor, total: null });
        setStatus(
          page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready',
        );
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
        if (seq !== requestSeq.current) return;
        setReference(error.requestId ?? null);
        setStatus('error');
      }
    },
    [observe],
  );

  useEffect(() => {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }, [load, appliedFilters, pages.cursor, pages.perPage]);

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    pages.first();
    setAppliedFilters(draftFilters);
  }

  function handleClearFilters(): void {
    setDraftFilters(BLANK_FILTERS);
    pages.first();
    setAppliedFilters(BLANK_FILTERS);
  }

  function retry(): void {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }

  function updateDraft(field: keyof FilterState) {
    return (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value } = event.target;
      setDraftFilters((current) => ({ ...current, [field]: value }));
    };
  }

  function describeDeleteFailure(error: ApiError): string {
    // The 409 (bound) and everything else read differently — see this
    // file's own docblock.
    if (error.status === 409) return t('codes.deleteConflict');
    return t('codes.deleteFailed');
  }

  function openDeleteDialog(node: DispensaryCode): void {
    setPendingDelete(node);
    setDeleteFailure(null);
    setDeleteReference(null);
  }

  function closeDeleteDialog(): void {
    if (deleting) return;
    setPendingDelete(null);
    setDeleteFailure(null);
    setDeleteReference(null);
  }

  async function confirmDelete(): Promise<void> {
    if (pendingDelete === null || deleting) return;
    const uuid = pendingDelete.uuid;
    setDeleting(true);
    setDeleteFailure(null);
    setDeleteReference(null);

    try {
      await codesApi.remove(uuid);
      setRows((current) => current.filter((row) => row.id !== uuid));
      onChanged?.();
      setPendingDelete(null);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      // NOT closeDeleteDialog(): kept open so the 409's own sentence stays
      // readable, the same choice ReferenceLifecycleActions.tsx makes.
      setDeleteFailure(describeDeleteFailure(error));
      setDeleteReference(error.requestId ?? null);
    } finally {
      setDeleting(false);
    }
  }

  /**
   * One handler for both, because the dialog reports one thing: the code as
   * it now stands. A correction REPLACES its row in place rather than
   * prepending a second copy — the uuid is unchanged, which is the whole
   * reason correcting beats delete-and-recreate.
   */
  function handleSaved(saved: DispensaryCode): void {
    setRows((current) =>
      current.some((row) => row.id === saved.uuid)
        ? current.map((row) => (row.id === saved.uuid ? toRow(saved) : row))
        : [toRow(saved), ...current],
    );
    if (status === 'empty') setStatus('ready');
    setFormOpen(false);
    setEditing(undefined);
    onChanged?.();
  }

  function renderKind(row: Row): ReactNode {
    return t(`codes.kind.${(row as unknown as DispensaryCode).kind}`);
  }

  function renderType(row: Row): ReactNode {
    return t(`codes.type.${(row as unknown as DispensaryCode).type_code}`);
  }

  function renderDispensary(row: Row): ReactNode {
    return optionalReferenceName((row as unknown as DispensaryCode).dispensary, i18n.language);
  }

  function renderState(row: Row): ReactNode {
    return t(`codes.state.${codeState(row as unknown as DispensaryCode)}`);
  }

  function renderActions(row: Row): ReactNode {
    const node = row as unknown as DispensaryCode;
    // Never offered on a bound row — see this file's own docblock.
    if (codeState(node) === 'bound') return null;
    return (
      <>
        <Button
          icon={<PencilSimpleIcon size={16} />}
          variant="ghost"
          onClick={() => {
            setEditing(node);
            setFormOpen(true);
          }}
        >
          {t('codes.edit')}
        </Button>
        <Button
          icon={<TrashIcon size={16} />}
          variant="ghost"
          onClick={() => {
            openDeleteDialog(node);
          }}
        >
          {t('codes.delete')}
        </Button>
      </>
    );
  }

  const kindOptions: readonly SelectOption[] = KINDS.map((value) => ({
    value,
    label: t(`codes.kind.${value}`),
  }));
  const typeOptions: readonly SelectOption[] = TYPES.map((value) => ({
    value,
    label: t(`codes.type.${value}`),
  }));
  const stateOptions: readonly SelectOption[] = [
    { value: 'free', label: t('codes.state.free') },
    { value: 'bound', label: t('codes.state.bound') },
  ];

  const columns: readonly Column[] = [
    { key: 'code', label: t('codes.columnCode'), neverMirrors: true },
    { key: 'kind', label: t('codes.columnKind'), render: renderKind },
    { key: 'type_code', label: t('codes.columnType'), render: renderType },
    { key: 'dispensary', label: t('codes.columnDispensary'), render: renderDispensary },
    { key: 'state', label: t('codes.columnState'), render: renderState },
    { key: 'actions', label: t('codes.columnActions'), render: renderActions },
  ];

  return (
    <div className={styles['page']}>
      {active ? (
        <PageActions>
          <Button
            icon={<PlusIcon size={16} />}
            onClick={() => {
              // Cleared explicitly: without this, adding a code straight after
              // correcting one opens the dialog pre-filled with the corrected
              // row and saves over it.
              setEditing(undefined);
              setFormOpen(true);
            }}
          >
            {t('codes.addCode')}
          </Button>
        </PageActions>
      ) : null}

      <FilterBar onSubmit={handleFilterSubmit} onClear={handleClearFilters}>
        <Input
          label={t('codes.filterDispensary')}
          width="compact"
          dir="ltr"
          value={draftFilters.dispensary}
          onChange={updateDraft('dispensary')}
        />
        <Select
          label={t('codes.filterKind')}
          width="compact"
          placeholder={t('codes.anyKind')}
          options={kindOptions}
          value={draftFilters.kind}
          onChange={updateDraft('kind')}
        />
        <Select
          label={t('codes.filterType')}
          width="compact"
          placeholder={t('codes.anyType')}
          options={typeOptions}
          value={draftFilters.type}
          onChange={updateDraft('type')}
        />
        <Select
          label={t('codes.filterState')}
          width="compact"
          placeholder={t('codes.anyState')}
          options={stateOptions}
          value={draftFilters.state}
          onChange={updateDraft('state')}
        />
      </FilterBar>

      <DataTable
        caption={t('codes.tabPool')}
        columns={columns}
        rows={rows}
        status={status}
        empty={{ body: t('codes.emptyPool') }}
        noResults={{ title: t('codes.noResultsPool') }}
        onClearFilters={handleClearFilters}
        error={{ title: t('codes.loadFailedPool') }}
      />

      <Pagination pages={pages} total={null} rowsOnPage={rows.length} />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button icon={<ArrowClockwiseIcon size={16} />} variant="secondary" onClick={retry}>
            {t('codes.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="codeListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <CodeFormDialog
        open={formOpen}
        {...(editing === undefined ? {} : { editing })}
        onClose={() => {
          setFormOpen(false);
          setEditing(undefined);
        }}
        onSaved={handleSaved}
      />

      {/* Review finding 3: destroying a free code is permanent, so it
          confirms inside a still-open `Dialog` — ReferenceLifecycleActions
          .tsx's own shape — rather than acting on the first click. */}
      <Dialog
        open={pendingDelete !== null}
        destructive
        title={t('codes.confirmDeleteTitle', { code: pendingDelete?.code ?? '' })}
        onClose={closeDeleteDialog}
        actions={
          <>
            <Button
              icon={<TrashIcon size={16} />}
              variant="destructive"
              loading={deleting}
              onClick={() => {
                void confirmDelete();
              }}
            >
              {t('codes.confirm')}
            </Button>
            <Button icon={<XIcon size={16} />} variant="ghost" onClick={closeDeleteDialog}>
              {t('codes.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['deleteBody']}>
          {t('codes.confirmDeleteBody', { code: pendingDelete?.code ?? '' })}
        </p>

        <div className={styles['deleteFailure']} role="alert" aria-atomic="true">
          {deleteFailure}
        </div>

        {deleteReference === null ? null : (
          <p className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid="codeDeleteRequestId">
              {deleteReference}
            </bdi>
          </p>
        )}
      </Dialog>
    </div>
  );
}
